/** Pure credential decisions with no AWS SDK: name-and-code error classification, and a single-flight refresh coordinator with a 5-minute pre-expiry window. */

/** Error names/codes that mean "credentials expired / could not be obtained". */
const CREDENTIAL_ERROR_NAMES: readonly string[] = [
  "ExpiredTokenException",
  "ExpiredToken",
  "CredentialsProviderError",
];
const CREDENTIAL_ERROR_CODES: readonly string[] = [
  "ExpiredToken",
  "CredentialsError",
];

/** True for a credential-expiry error by name or code only; message matching caused spurious refresh retries. */
export function isCredentialError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: string; code?: string };
  return (
    (typeof e.name === "string" && CREDENTIAL_ERROR_NAMES.includes(e.name)) ||
    (typeof e.code === "string" && CREDENTIAL_ERROR_CODES.includes(e.code))
  );
}

/** Refresh if credentials expire within this window (ms). */
export const REFRESH_WINDOW_MS = 5 * 60 * 1000;

/** A minted credential set with its expiry. Opaque `value` — adapter-defined. */
export interface CredentialEntry<T = unknown> {
  /** The credential payload the transport will use. */
  value: T;
  /** Absolute expiry time, epoch ms. */
  expiresAt: number;
}

/** A function that mints a fresh credential set. Supplied by the adapter. */
export type CredentialMinter<T> = () => Promise<CredentialEntry<T>>;

/** The credential lifecycle coordinator. */
export interface CredentialLifecycle<T> {
  /** Return a valid credential set, reusing the cache outside the refresh window; concurrent calls share one in-flight mint. */
  get(): Promise<CredentialEntry<T>>;
  /** Force a fresh mint for the uploader's single-shot retry; parallel calls still collapse into one. */
  forceRefresh(): Promise<CredentialEntry<T>>;
  /** Drop the cached entry. The next `get()` mints fresh. */
  invalidate(): void;
}

/** Create the coordinator; `minter` mints a credential set and `now` is the injectable clock. */
export function createCredentialLifecycle<T>(
  minter: CredentialMinter<T>,
  now: () => number = Date.now,
): CredentialLifecycle<T> {
  let cached: CredentialEntry<T> | null = null;
  /** The in-flight mint, if one is running — the single-flight dedup handle. */
  let inFlight: Promise<CredentialEntry<T>> | null = null;

  /** True when `entry` is still safely usable (outside the refresh window). */
  const isFresh = (entry: CredentialEntry<T>): boolean =>
    entry.expiresAt - now() > REFRESH_WINDOW_MS;

  /** Run a single deduped mint; cache the result; clear `inFlight` either way. */
  const mintOnce = (): Promise<CredentialEntry<T>> => {
    if (inFlight) return inFlight;
    inFlight = minter()
      .then((entry) => {
        cached = entry;
        return entry;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  return {
    get(): Promise<CredentialEntry<T>> {
      if (cached && isFresh(cached)) return Promise.resolve(cached);
      return mintOnce();
    },
    forceRefresh(): Promise<CredentialEntry<T>> {
      // Drop the stale entry first so a get() racing the refresh can't return it.
      cached = null;
      return mintOnce();
    },
    invalidate(): void {
      cached = null;
    },
  };
}
